import React, { Suspense, useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  WindStationService,
  WindStation,
} from "@/services/WindStationService";
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  SignalIcon,
  TableCellsIcon,
  MapIcon,
} from "@heroicons/react/24/outline";
import PushStationsStatusView from "@/components/wind/PushStationsStatusView";
import { SourceBadge } from "@/components/wind/stationDisplay";

// Leaflet only loads when the map is shown (and stays out of the Jest module graph).
const WindStationsMap = React.lazy(() => import("./WindStationsMap"));

const SOURCES = ["all", "metar", "ndbc", "knmi", "pws", "synthetic"] as const;
type SourceFilter = (typeof SOURCES)[number];
type ViewMode = "table" | "map";
type StaleFilter = "all" | "exclude" | "only";
const PAGE_SIZE = 25;
/** "Load all": one request big enough for the whole online catalog (~6.4k stations). */
const LOAD_ALL = 10000;

const inputClass =
  "w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500";

/** m/s helper — the list already gives knots; keep a converter for the detail panel. */
const ktsToMs = (kts?: number) => (kts == null ? undefined : kts / 1.94384);

const fmtAge = (epochMs?: number): string => {
  if (!epochMs) return "—";
  const mins = Math.round((Date.now() - epochMs) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m ago`;
};

const WindStationsPage: React.FC = () => {
  // SRF-2749: data-source switch — the Cloudflare live-wind plane (primary, default) vs the
  // deprecated Postgres pull-station registry. SRF-2813: cf-pull + cf-push collapsed into
  // one "cloudflare" view. The tab lives in ?tab= so links and Back work; switching tabs
  // pushes a history entry and drops the other tab's table params.
  const [searchParams, setSearchParams] = useSearchParams();
  const stationSource: "postgres" | "cloudflare" =
    searchParams.get("tab") === "postgres" ? "postgres" : "cloudflare";
  const setStationSource = (key: "postgres" | "cloudflare") => {
    if (key === stationSource) return;
    setSearchParams(key === "cloudflare" ? {} : { tab: key });
  };
  const [stations, setStations] = useState<WindStation[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [staleFilter, setStaleFilter] = useState<StaleFilter>("all");
  const [limit, setLimit] = useState(LOAD_ALL);
  const [selected, setSelected] = useState<WindStation | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("table");
  const [page, setPage] = useState(0);
  // Current map viewport, reported by the map; used by "Load in current view".
  const [mapBounds, setMapBounds] = useState<
    { minlat: number; maxlat: number; minlon: number; maxlon: number } | null
  >(null);
  const [scope, setScope] = useState<"world" | "view">("world");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    null
  );

  /** Load the whole world (default) or the current map viewport when a bbox is given. */
  const load = useCallback(
    async (bbox?: { minlat: number; maxlat: number; minlon: number; maxlon: number }) => {
      setLoading(true);
      try {
        const data = await WindStationService.list(limit, bbox);
        setStations(data);
        setScope(bbox ? "view" : "world");
      } catch {
        setMessage({ type: "error", text: "Failed to load wind stations." });
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  useEffect(() => {
    // The legacy registry is only fetched when its tab is actually opened.
    if (stationSource === "postgres") load();
  }, [load, stationSource]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  // Client-side filter over the loaded set (server already caps by limit).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stations.filter((s) => {
      if (source !== "all" && s.source !== source) return false;
      if (staleFilter === "only" && !s.stale) return false;
      if (staleFilter === "exclude" && s.stale) return false;
      if (q && !(s.name || "").toLowerCase().includes(q) && !s.id.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [stations, query, source, staleFilter]);

  // Reset to the first page whenever the filtered set changes.
  useEffect(() => {
    setPage(0);
  }, [query, source, staleFilter, view]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE),
    [filtered, currentPage]
  );

  const counts = useMemo(() => {
    const bySource: Record<string, number> = {};
    let stale = 0;
    for (const s of stations) {
      bySource[s.source] = (bySource[s.source] || 0) + 1;
      if (s.stale) stale++;
    }
    return { total: stations.length, bySource, stale };
  }, [stations]);

  const handleRefresh = async (s: WindStation) => {
    setRefreshingId(s.id);
    try {
      const updated = await WindStationService.refresh(s.id);
      setStations((prev) => prev.map((x) => (x.id === s.id ? { ...x, ...updated } : x)));
      // Functional update: only merge if this station is still the selected one
      // (the user may have clicked away while the refresh was in flight).
      setSelected((cur) => (cur && cur.id === s.id ? { ...cur, ...updated } : cur));
      setMessage({ type: "success", text: `Refreshed ${s.name || s.id}.` });
    } catch {
      setMessage({
        type: "error",
        text: "Refresh only works for PWS stations; others are updated by ingestion jobs.",
      });
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* SRF-2749 source tabs: Postgres pull stations vs the Cloudflare live-wind plane */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="flex gap-1 -mb-px" role="tablist" aria-label="Station data source">
          {/* SRF-2813: the two Cloudflare tabs were split by INGEST METHOD, which made one
              physical station appear twice — a pull station showed as its live pull row and
              as a push credential that could never have data. Ingest is now a filter inside
              a single list. The Postgres tab is a different view (registry-only), not a
              third copy of the same stations. */}
          {(
            [
              ["cloudflare", "Stations (Cloudflare)"],
              ["postgres", "Postgres (legacy)"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={stationSource === key}
              onClick={() => setStationSource(key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                stationSource === key
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {stationSource === "cloudflare" ? (
        <PushStationsStatusView />
      ) : (
      <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Legacy stations (Postgres)</h1>
          <p className="text-sm text-gray-400 mt-1">
            Deprecated registry view — the Cloudflare tab is the source of truth for live
            wind. Kept for comparison and PWS refreshes.
          </p>
        </div>
        {/* Table / Map tab toggle */}
        <div className="inline-flex rounded-md border border-gray-700 overflow-hidden shrink-0">
          <button
            onClick={() => setView("table")}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${
              view === "table" ? "bg-gray-700 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"
            }`}
          >
            <TableCellsIcon className="w-4 h-4" /> Table
          </button>
          <button
            onClick={() => setView("map")}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-l border-gray-700 ${
              view === "map" ? "bg-gray-700 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"
            }`}
          >
            <MapIcon className="w-4 h-4" /> Map
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 rounded-md px-4 py-2 text-sm ${
            message.type === "success"
              ? "bg-green-500/15 text-green-300"
              : "bg-red-500/15 text-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className="rounded bg-gray-800 border border-gray-700 px-2.5 py-1 text-gray-300">
          {counts.total} loaded
        </span>
        {Object.entries(counts.bySource).map(([src, n]) => (
          <span key={src} className="rounded bg-gray-800 border border-gray-700 px-2.5 py-1">
            <SourceBadge source={src} /> <span className="text-gray-300">{n}</span>
          </span>
        ))}
        {counts.stale > 0 && (
          <span className="rounded bg-red-500/10 border border-red-700/40 px-2.5 py-1 text-red-300">
            {counts.stale} stale
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className={`${inputClass} pl-9`}
            aria-label="Search legacy stations"
            placeholder="Search by name or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className={`${inputClass} sm:w-40`}
          aria-label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value as SourceFilter)}
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "All sources" : s}
            </option>
          ))}
        </select>
        <select
          className={`${inputClass} sm:w-44`}
          aria-label="Load limit"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        >
          {[200, 500, 1000, 2000].map((n) => (
            <option key={n} value={n}>
              Load {n}
            </option>
          ))}
          <option value={LOAD_ALL}>Load all (~6.4k)</option>
        </select>
        <select
          className={`${inputClass} sm:w-40`}
          aria-label="Freshness"
          value={staleFilter}
          onChange={(e) => setStaleFilter(e.target.value as StaleFilter)}
          title="Filter by observation freshness"
        >
          <option value="all">All stations</option>
          <option value="exclude">Exclude stale</option>
          <option value="only">Only stale</option>
        </select>
        {view === "map" && (
          <button
            onClick={() => (mapBounds ? load(mapBounds) : undefined)}
            disabled={!mapBounds || loading}
            title="Reload stations inside the current map viewport"
            className="inline-flex items-center gap-2 rounded-md bg-gray-700 hover:bg-gray-600 px-3 py-2 text-sm text-white disabled:opacity-40 whitespace-nowrap"
          >
            <MapIcon className="w-4 h-4" /> Load in view
          </button>
        )}
        <button
          onClick={() => load()}
          className="inline-flex items-center gap-2 rounded-md bg-gray-700 hover:bg-gray-600 px-3 py-2 text-sm text-white"
          title="Reload the whole world"
        >
          <ArrowPathIcon className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* List / Map */}
        <div className="flex-1 min-w-0">
        {view === "map" ? (
          <Suspense
            fallback={
              <div className="flex h-[70vh] items-center justify-center rounded-lg border border-gray-700 text-gray-400">
                Loading map…
              </div>
            }
          >
            <WindStationsMap
              stations={filtered}
              selectedId={selected?.id || null}
              onSelect={setSelected}
              onBoundsChange={setMapBounds}
            />
          </Suspense>
        ) : (
        <div className="overflow-auto rounded-lg border border-gray-700 max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Station</th>
                <th className="px-4 py-2 text-left font-medium">Source</th>
                <th className="px-4 py-2 text-right font-medium">Wind</th>
                <th className="px-4 py-2 text-right font-medium">Gust</th>
                <th className="px-4 py-2 text-right font-medium">Dir</th>
                <th className="px-4 py-2 text-left font-medium">Observed</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 text-gray-300">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                    Loading stations…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                    No stations match the current filters.
                  </td>
                </tr>
              ) : (
                paged.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className={`cursor-pointer hover:bg-gray-700/40 ${
                      selected?.id === s.id ? "bg-gray-700/50" : ""
                    }`}
                  >
                    <td className="px-4 py-2">
                      <div className="text-white">{s.name || s.id}</div>
                      <div className="font-mono text-xs text-gray-500">{s.id}</div>
                    </td>
                    <td className="px-4 py-2">
                      <SourceBadge source={s.source} />
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {s.windKts != null ? `${s.windKts.toFixed(1)} kt` : "—"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                      {s.gustKts != null ? `${s.gustKts.toFixed(1)}` : "—"}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                      {s.windDir != null ? `${Math.round(s.windDir)}°` : "—"}
                    </td>
                    <td className={`px-4 py-2 ${s.stale ? "text-red-400" : "text-gray-400"}`}>
                      {fmtAge(s.updatedAt)}
                      {s.stale && " (stale)"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {s.source === "pws" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefresh(s);
                          }}
                          disabled={refreshingId === s.id}
                          className="text-blue-400 hover:text-blue-300 disabled:opacity-50"
                          title="Refresh this PWS station"
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Pagination (table view only) + result count */}
        {view === "table" && !loading && filtered.length > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
            <span>
              {currentPage * PAGE_SIZE + 1}–
              {Math.min(filtered.length, (currentPage + 1) * PAGE_SIZE)} of {filtered.length}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="rounded border border-gray-700 px-2 py-1 disabled:opacity-40 hover:bg-gray-700"
                >
                  Prev
                </button>
                <span className="tabular-nums">
                  Page {currentPage + 1} / {pageCount}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={currentPage >= pageCount - 1}
                  className="rounded border border-gray-700 px-2 py-1 disabled:opacity-40 hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        {view === "map" && (
          <p className="mt-2 text-xs text-gray-500">
            {filtered.length} stations shown ({scope === "world" ? "worldwide" : "current view"})
            {counts.total >= limit && scope === "world" && (
              <span className="text-amber-400">
                {" "}· capped at {limit.toLocaleString()} — zoom in and use “Load in view” for
                more detail
              </span>
            )}
            . Click a marker for details. Arrow points where the wind blows; number is speed in
            knots. Gray = stale.
          </p>
        )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="lg:w-96 shrink-0 rounded-lg border border-gray-700 bg-gray-800/50 p-4 h-fit">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {selected.name || selected.id}
                </h2>
                <div className="font-mono text-xs text-gray-500">{selected.id}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-500 hover:text-gray-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <SourceBadge source={selected.source} />
              <span
                className={`inline-flex items-center gap-1 text-xs ${
                  selected.stale ? "text-red-400" : "text-green-400"
                }`}
              >
                <SignalIcon className="w-3.5 h-3.5" />
                {selected.stale ? "stale" : "live"}
              </span>
            </div>

            <dl className="space-y-2 text-sm">
              <Row label="Wind">
                {selected.windKts != null
                  ? `${selected.windKts.toFixed(1)} kt  ·  ${ktsToMs(selected.windKts)!.toFixed(
                      1
                    )} m/s`
                  : "—"}
              </Row>
              <Row label="Gust">
                {selected.gustKts != null ? `${selected.gustKts.toFixed(1)} kt` : "—"}
              </Row>
              <Row label="Direction">
                {selected.windDir != null ? `${Math.round(selected.windDir)}°` : "—"}
              </Row>
              <Row label="Coordinates">
                {selected.lat.toFixed(4)}, {selected.lon.toFixed(4)}
              </Row>
              <Row label="Observed">{fmtAge(selected.updatedAt)}</Row>
              <Row label="Fetched">{fmtAge(selected.fetchedAt)}</Row>
            </dl>

            {selected.source === "synthetic" && (
              <p className="mt-4 text-xs text-purple-300/80">
                Synthetic station (dev simulator). Edit its values from{" "}
                <span className="text-purple-200">Test Data → Synthetic wind</span>.
              </p>
            )}
            {selected.source === "pws" && (
              <button
                onClick={() => handleRefresh(selected)}
                disabled={refreshingId === selected.id}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm text-white disabled:opacity-50"
              >
                <ArrowPathIcon className="w-4 h-4" />
                {refreshingId === selected.id ? "Refreshing…" : "Refresh observation"}
              </button>
            )}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-gray-400">{label}</dt>
    <dd className="text-white text-right tabular-nums">{children}</dd>
  </div>
);

export default WindStationsPage;
